import { logError } from '@zg-rentals/log-error';

import DevToolActions from '../actions/DevToolActions';

import { API_LIST_ITEM_SOURCE } from '../reducers/devTool';

import { formatError } from '../utils/errorUtils';

import StringConstants from '../constants/StringConstants';
import { RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX } from '../constants/RouteConstants';

import ErrorRMW from '../model/ErrorRMW';

import { client } from '../utils/requesters';
import { updateUserCookies } from '../slices/user';

import type { Dispatch } from 'react';
import type { RestMethodNameProps } from '../constants/StringConstants';
import type { ErrorRMWProps } from '../model/ErrorRMW';

type Props = {
  request?: typeof client;
  requestConfig: { zgApi: string; methodName: RestMethodNameProps; requestHeaders: Record<string, string> };
  endpoint: string;
  config?: { params?: Record<string, unknown>; data?: Record<string, unknown>; timeout?: number };
  dispatch?: Dispatch<unknown>;
};

export type MakeRequestError = {
  success: boolean;
  error: ErrorRMWProps;
  errors: Array<ErrorRMWProps>;
  response: unknown;
};

const makeRequest = async <T>({
  request = client,
  requestConfig,
  endpoint,
  config = {},
  dispatch = () => {},
}: Props) => {
  const { zgApi, methodName = StringConstants.GET, requestHeaders } = requestConfig;

  const combinedConfig = {
    headers: {
      ...requestHeaders,
    },
    ...config,
  };

  try {
    const response = await request(zgApi)[methodName](
      endpoint,
      config.data ? config.data : combinedConfig,
      config.data ? combinedConfig : undefined,
    );

    if (endpoint.includes(RENTAL_GROWTH_GRAPHQL_API_V1_PREFIX)) {
      return response;
    }

    if (response?.data?.success === false) {
      if (response?.data?.error?.message) {
        throw new Error(response.data.error.message);
      } else {
        // @ts-expect-error
        throw new Error(response);
      }
    }

    dispatch(
      DevToolActions.addDevToolApiList({
        endpoint,
        source: __BROWSER__ ? API_LIST_ITEM_SOURCE.CLIENT : API_LIST_ITEM_SOURCE.MIDDLEWARE,
      }),
    );

    // update server side request cookies based on api response
    // used by renderer.js to return the correct cookies in header of o.g. request
    if (response.headers['set-cookie']) {
      dispatch(
        updateUserCookies({
          apiResponseServerSideRequestCookies: response.headers['set-cookie'],
        }),
      );
    }

    return response.data.response;
  } catch (err) {
    const data = err && err.response ? err.response.data : undefined;

    /**
     * NOTE fix for 10,000 byte limit for logger needs to happen after `data` is referenced
     * related issue: https://zillowgroup.atlassian.net/browse/ZRM-13946
     */
    err.config = '';
    err.request = '';
    err.response = '';

    logError({
      error: err,
      errorType: 'HTTPError',
      context: {
        endpoint,
        method: methodName,
      },
    });

    const { errorCopy, rawErrorsCopy } = formatError(data);

    // normalize error
    const normalizedErrorCopy = ErrorRMW(errorCopy);

    throw {
      success: false,
      error: normalizedErrorCopy,
      errors: rawErrorsCopy,
      response: data ? data.response : {},
    };
  }
};

export default makeRequest;
